import { useMemo } from 'react';
import { scoring, type RiskIndicator, type TrafficLight } from '@tragfaehigkeit/core';
import { de } from '../../../lib/i18n/de';
import { useWizardAudience } from '../../../components/wizard/WizardAudienceContext';
import {
  buildFinancialDatasetFromDraft,
  computeBgGapCentsFromDraft,
} from '../../../lib/store/scoreInput';
import type { ResourcesDraft } from '../../../lib/store/wizard';

interface ResourcesLiveScoreProps {
  resources: ResourcesDraft;
  caseId: string;
}

/**
 * Live-Score-Footer für die Resources-Sub-Steps.
 *
 * Zeigt den aktuellen Resources-Score (0-100), die Ampelfarbe und die wichtigsten
 * 1-3 Risikoindikatoren. Reagiert ohne Persistenz-Roundtrip auf Eingaben.
 */
export function ResourcesLiveScore({ resources, caseId }: ResourcesLiveScoreProps) {
  const t = de.steps.resources.liveScore;
  const audience = useWizardAudience();

  const { score, light, indicators } = useMemo(() => {
    const fd = buildFinancialDatasetFromDraft(resources, caseId);
    const dimScore = scoring.computeResourcesScore(fd);
    const bgGap = computeBgGapCentsFromDraft(resources);
    void bgGap; // BG-Gap fließt in den Private-Score, nicht in Resources direkt
    const tl = scoring.getTrafficLight(dimScore);
    const risks = scoring.getResourcesRiskIndicators(fd);
    return { score: dimScore, light: tl, indicators: risks };
  }, [resources, caseId]);

  // Mandanten-Sicht: KEIN Finanz-Score, keine Ampel, keine Risiken. Neutrales
  // Erfassungs-Signal (Art. 22 DSGVO — Bewertung liegt beim Berater).
  if (audience === 'mandant') {
    return (
      <aside className="mt-8 rounded-sm border-l-4 border-stone-200 bg-stone-50/60 px-6 py-4 md:px-8">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.hub.liveScore.mandantSectionLabel}
        </p>
        <p className="mt-2 font-serif text-sm text-stone-700">{de.hub.liveScore.mandantRecorded}</p>
        <p className="mt-1 font-serif text-xs text-stone-500">
          {de.hub.liveScore.mandantEvaluationNote}
        </p>
      </aside>
    );
  }

  const top = indicators.slice(0, 3);

  return (
    <aside
      aria-label={t.sectionLabel}
      className="mt-8 rounded-sm border border-stone-200 bg-stone-50/60 px-6 py-6 md:px-8"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-10">
        <div className="md:w-56 md:flex-shrink-0">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {t.scoreLabel}
          </p>
          <div className="mt-3 flex items-baseline gap-2">
            <span
              className="font-serif text-5xl font-black tabular-nums tracking-tight text-stone-900 md:text-6xl"
              data-testid="live-score-value"
            >
              {score}
            </span>
            <span className="font-serif text-base italic text-stone-500">{t.ofLabel}</span>
          </div>
          <TrafficBadge light={light} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {t.risksLabel}
          </p>
          {top.length === 0 ? (
            <p className="mt-3 font-serif text-sm italic text-stone-500">{t.noRisks}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {top.map((r) => (
                <RiskIndicatorRow key={r.code} indicator={r} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
}

function TrafficBadge({ light }: { light: TrafficLight }) {
  const t = de.steps.resources.liveScore;
  const map: Record<TrafficLight, { label: string; dot: string; text: string }> = {
    green: { label: t.trafficGreen, dot: 'bg-emerald-500', text: 'text-emerald-700' },
    yellow: { label: t.trafficYellow, dot: 'bg-amber-500', text: 'text-amber-700' },
    red: { label: t.trafficRed, dot: 'bg-rose-500', text: 'text-rose-700' },
  };
  const v = map[light];
  return (
    <span
      data-testid="live-score-light"
      className={`mt-3 inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-widest ${v.text}`}
    >
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${v.dot}`} />
      {v.label}
    </span>
  );
}

function RiskIndicatorRow({ indicator }: { indicator: RiskIndicator }) {
  const sevColor =
    indicator.severity === 'critical'
      ? 'text-rose-700'
      : indicator.severity === 'warning'
        ? 'text-amber-700'
        : 'text-stone-600';
  const sevDot =
    indicator.severity === 'critical'
      ? 'bg-rose-500'
      : indicator.severity === 'warning'
        ? 'bg-amber-500'
        : 'bg-stone-400';
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${sevDot}`}
      />
      <span className={`font-serif text-sm leading-snug ${sevColor}`}>{indicator.message}</span>
    </li>
  );
}
